import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ContentProgramAssignment {
  program_id: string;
  program_name: string;
  program_status: string | null;
  chapters: { id: string; name: string }[];
}

/**
 * Returns the list of programs (Rutas) where a given content_bank item
 * has been added, grouped by program, with the chapters it appears in.
 */
export function useContentPrograms(contentId: string | null | undefined) {
  return useQuery({
    queryKey: ['content-programs', contentId],
    enabled: !!contentId,
    queryFn: async (): Promise<ContentProgramAssignment[]> => {
      const { data, error } = await (supabase as any)
        .from('chapter_contents')
        .select(
          'chapter_id, program_chapters!inner(id, name, program_id, programs!inner(id, name, status))'
        )
        .eq('content_id', contentId);

      if (error) throw error;

      const grouped = new Map<string, ContentProgramAssignment>();
      (data ?? []).forEach((row: any) => {
        const ch = row.program_chapters;
        if (!ch?.programs) return;
        const prog = ch.programs;
        if (!grouped.has(prog.id)) {
          grouped.set(prog.id, {
            program_id: prog.id,
            program_name: prog.name,
            program_status: prog.status ?? null,
            chapters: [],
          });
        }
        grouped.get(prog.id)!.chapters.push({ id: ch.id, name: ch.name });
      });

      return Array.from(grouped.values()).sort((a, b) =>
        a.program_name.localeCompare(b.program_name)
      );
    },
  });
}
